import { useEffect, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  ROLE_LABELS,
  type InvitationPreview,
  type SessionResponse,
} from "@velivo/shared";
import { AuthLayout } from "../components/AuthLayout";
import { Field } from "../components/Field";
import { useAuth } from "../auth/AuthContext";
import { api, ApiRequestError } from "../api/client";

/**
 * DECISIONS.md AUTH-002 / API_SPEC.md §4.1 — accepting an invitation is what
 * materializes the membership. Unauthenticated by necessity: the invitee may
 * not have an account yet.
 */
export function AcceptInvitationPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { applySession, refreshUser, user } = useAuth();
  const token = params.get("token") ?? "";

  const [preview, setPreview] = useState<InvitationPreview | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!token) {
      setLoadError("This invitation link is missing its token.");
      return;
    }
    api<InvitationPreview>(`/invitations/${encodeURIComponent(token)}`, {
      auth: false,
    })
      .then(setPreview)
      .catch((caught) =>
        setLoadError(
          caught instanceof ApiRequestError
            ? caught.message
            : "Could not load this invitation.",
        ),
      );
  }, [token]);

  async function handleSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setFieldErrors({});
    setSubmitting(true);
    try {
      if (preview?.requiresAccountSetup) {
        // New account: acceptance creates the user and returns a session.
        const session = await api<SessionResponse>(
          `/invitations/${encodeURIComponent(token)}/accept`,
          { method: "POST", body: { name, password }, auth: false },
        );
        applySession(session);
      } else {
        // Existing account: the API refuses to mint a session from an
        // invitation link alone, so this call is made with the signed-in
        // user's own credentials and just adds the grant.
        await api(`/invitations/${encodeURIComponent(token)}/accept`, {
          method: "POST",
          body: {},
        });
        await refreshUser();
      }
      navigate("/", { replace: true });
    } catch (caught) {
      if (caught instanceof ApiRequestError) {
        const fields = caught.fieldErrors();
        if (Object.keys(fields).length > 0) setFieldErrors(fields);
        else setError(caught.message);
      } else {
        setError("Could not reach the server. Try again.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loadError) {
    return (
      <AuthLayout
        title="Invitation not available"
        subtitle={loadError}
        footer={<Link to="/login">Back to sign in</Link>}
      >
        <Link className="btn btn--secondary btn--block" to="/login">
          Back to sign in
        </Link>
      </AuthLayout>
    );
  }

  if (!preview) {
    return (
      <AuthLayout title="Loading invitation…">
        <div
          className="skeleton"
          style={{ height: 20, marginBottom: "var(--space-3)" }}
        />
        <div
          className="skeleton"
          style={{ height: 44, marginBottom: "var(--space-3)" }}
        />
        <div className="skeleton" style={{ height: 44 }} />
      </AuthLayout>
    );
  }

  const scope = preview.brandName
    ? `${preview.brandName} (${preview.organizationName})`
    : preview.organizationName;

  // An existing account must authenticate before the grant is applied — the
  // invitation link is not proof of identity (see the API's SIGN_IN_TO_ACCEPT).
  if (!preview.requiresAccountSetup && !user) {
    return (
      <AuthLayout
        title="Sign in to accept"
        subtitle={
          <>
            You already have a Echo account. Sign in as{" "}
            <strong>{preview.email}</strong> to join <strong>{scope}</strong> as{" "}
            <strong>{ROLE_LABELS[preview.role]}</strong>.
          </>
        }
      >
        <Link
          className="btn btn--primary btn--block"
          to={`/login?next=${encodeURIComponent(`/accept-invitation?token=${token}`)}`}
        >
          Sign in
        </Link>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Accept your invitation"
      subtitle={
        <>
          You've been invited to <strong>{scope}</strong> as{" "}
          <strong>{ROLE_LABELS[preview.role]}</strong>.
        </>
      }
      footer={<Link to="/login">Sign in instead</Link>}
    >
      <form onSubmit={handleSubmit} noValidate>
        {error && (
          <div className="alert alert--error" role="alert">
            {error}
          </div>
        )}

        <Field
          label="Email"
          type="email"
          value={preview.email}
          readOnly
          disabled
        />

        {preview.requiresAccountSetup && (
          <>
            <Field
              label="Your name"
              name="name"
              autoComplete="name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={fieldErrors.name}
              required
            />
            <Field
              label="Create a password"
              type="password"
              name="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              help="At least 12 characters."
              error={fieldErrors.password}
              required
            />
          </>
        )}

        <button
          type="submit"
          className="btn btn--primary btn--block"
          disabled={submitting}
        >
          {submitting ? "Joining…" : "Accept invitation"}
        </button>
      </form>
    </AuthLayout>
  );
}
